/**
 * Class for detecting and categorizing terminal errors
 * Centralizes all error pattern detection logic
 */
export class ErrorPatterns {
  // Error pattern detection
  static hasErrorPatterns(output: string): boolean {
    return (
      /(error|failed|unable to|exception|Error|Failed)/i.test(output) ||
      /TypeError:|ReferenceError:|SyntaxError:/i.test(output) ||
      /\[vite\] Internal server error/i.test(output)
    );
  }

  // Detect errors in real-time terminal output
  static hasRealTimeError(output: string): boolean {
    return (
      /\[vite\] Internal server error/i.test(output) ||
      /\[\w+\] (error|failed)/i.test(output) || // Format like "[plugin] error"
      /error: /i.test(output) ||
      /TypeError:|ReferenceError:|SyntaxError:/i.test(output) ||
      /Failed to compile/i.test(output) ||
      /Module not found/i.test(output) ||
      /Cannot find module/i.test(output) ||
      /ERR!/i.test(output)
    );
  }

  // Check if this is a benign error that should be ignored
  static isBenignError(message: string): boolean {
    console.log('Checking for benign error patterns');

    try {
      const benignPatterns = [
        /warning.*deprecated/i,
        /npm notice/i,
        /optional dependency.*failed/i,
        /peer dependency/i,
        /warning .* latest version is/i,
        /Restoring pack failed from/i,
      ];

      return benignPatterns.some((pattern) => pattern.test(message));
    } catch (error) {
      console.error('Error checking benign error patterns:', error);
      return false;
    }
  }

  // Detect Vite-specific errors
  static isViteError(output: string): boolean {
    return output.includes('[vite]') || output.includes('vite:') || /Failed to compile/i.test(output);
  }

  // Determine error severity based on patterns and exit code
  static determineErrorSeverity(output: string, exitCode: number): 'error' | 'warning' | 'info' {
    return exitCode > 1 || /(cannot find module|TypeError|ReferenceError|SyntaxError)/i.test(output)
      ? 'error'
      : exitCode === 1
        ? 'warning'
        : 'info';
  }
}
